import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Facebook, MessageCircle, Info, Share2 } from "lucide-react";
import { LegalLinks } from "@/components/Legal";
import { api } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { DeveloperCard } from "@/components/DeveloperCard";
import { APP_VERSION } from "@/lib/version";

type About = { text_ar: string; text_en: string; facebook: string; whatsapp: string };

const LinkRow = ({ href, icon: Icon, label, color, id }: { href: string; icon: any; label: string; color: string; id: string }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" data-testid={id}
    className="flex items-center gap-3 rounded-2xl border bg-card px-4 py-3 font-bold transition-transform hover:-translate-y-0.5 active:scale-[0.98]">
    <span className={`grid h-9 w-9 place-items-center rounded-xl text-white ${color}`}><Icon className="h-5 w-5" /></span>{label}
  </a>
);

export default function AboutPage() {
  const { t, lang } = usePrefs();
  const nav = useNavigate();
  const loc = useLocation();
  const [a, setA] = useState<About | null>(null);
  useEffect(() => { api.get("/platform/about").then((r) => setA(r.data)).catch(() => setA({ text_ar: "", text_en: "", facebook: "", whatsapp: "" })); }, []);
  const text = (lang === "en" && a?.text_en) || a?.text_ar || t("about.default");
  return (
    <div data-testid="about-page" className="min-h-screen bg-background px-5 py-6 max-w-xl mx-auto space-y-6 animate-in fade-in duration-500">
      <button onClick={() => (loc.key === "default" ? nav("/") : nav(-1))} data-testid="about-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground">
        <ArrowRight className="h-4 w-4 ltr:rotate-180" />{t("common.back")}
      </button>
      <div className="flex items-center gap-4">
        <img src="/icon-192.png" alt="" className="h-16 w-16 rounded-2xl shadow" onError={(e) => ((e.target as HTMLImageElement).style.display = "none")} />
        <div>
          <h1 className="text-4xl font-black">{t("about.title")}</h1>
          <p data-testid="about-version" className="text-sm text-muted-foreground mt-1" dir="auto">{t("about.version")} <span dir="ltr">{APP_VERSION}</span></p>
        </div>
      </div>
      <p data-testid="about-text" className="rounded-3xl border bg-card p-5 leading-8 whitespace-pre-line">{text}</p>
      {a && (a.whatsapp || a.facebook) && (
        <section className="space-y-2">
          <h2 className="text-base md:text-lg font-bold">{t("about.contact")}</h2>
          {a.whatsapp && <LinkRow id="about-whatsapp" href={`https://wa.me/${a.whatsapp}`} icon={MessageCircle} label={t("about.whatsapp")} color="bg-[#25D366]" />}
          {a.facebook && <LinkRow id="about-facebook" href={a.facebook} icon={Facebook} label={t("about.facebook")} color="bg-[#1877F2]" />}
        </section>
      )}
      <a href={`https://wa.me/?text=${encodeURIComponent(t("about.shareMsg") + " " + window.location.origin)}`} target="_blank" rel="noopener noreferrer" data-testid="about-share-whatsapp"
        className="h-12 w-full rounded-full bg-[#25D366] text-white font-bold inline-flex items-center justify-center gap-2 hover:bg-[#1ebe5b] active:scale-[0.98] transition-transform">
        <Share2 className="h-5 w-5" />{t("about.share")}
      </a>
      <DeveloperCard />
      <LegalLinks className="justify-center" />
    </div>
  );
}

export const AboutLink = ({ className = "" }: { className?: string }) => {
  const { t } = usePrefs();
  const nav = useNavigate();
  return (
    <button type="button" onClick={() => nav("/about")} data-testid="about-link" className={`inline-flex items-center gap-1.5 font-semibold text-muted-foreground hover:text-foreground ${className}`}>
      <Info className="h-4 w-4" />{t("about.title")}
    </button>
  );
};
